// `avatar status [--json]` — Command 06 spec.
// T5 (#247): nhánh --json mang thêm `schema_version` + `auth` (D8) cho panel login
// của extension. Đường text giữ nguyên byte-for-byte.
// Read-only snapshot: project name, CLI version, pack version, pending count,
// backup count, tech-stack first-line. No mutations.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import boxen from "boxen";
import type { Command } from "commander";
import { discoverEnabledAndAvailableTools } from "../lib/discover-pack-tools-and-defaults.js";
import { pathExists, readText } from "../lib/filesystem-helpers.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { listBackups } from "../lib/pack-backup-manager.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { readPinnedPackVersion } from "../lib/team-pack-manager.js";
import { chalk, log, setMachineReadableMode } from "../lib/terminal-logger.js";
import { readUserConfig } from "../lib/user-config-store.js";

export function registerStatusCommand(program: Command): void {
  program
    .command("status")
    .description("Snapshot tức thì: project, pack version, pending, backup")
    .option("--json", "Output JSON cho script")
    .action(async (opts: { json?: boolean }) => {
      try {
        // #280: luật 4 của hợp đồng — stdout chỉ chứa JSON. Bật trước gatherStatus
        // vì nó (và gatherAuthStatus) có thể log cảnh báo người-đọc.
        if (opts.json) setMachineReadableMode(true);
        const snapshot = await gatherStatus(process.cwd());
        if (opts.json) {
          // D8: trạng thái login nhét vào chính `status --json`, không đẻ namespace
          // mới. Chỉ tính ở nhánh --json — đường text KHÔNG được đổi (AC8) và không
          // được trả giá đọc thêm file cho thứ nó không in.
          writeMachineReadableJson({ ...snapshot, auth: await gatherAuthStatus() });
        } else {
          renderStatusBox(snapshot);
        }
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

interface StatusSnapshot {
  projectName: string;
  cliVersion: string;
  packVersion: string | null;
  pendingCount: number;
  backupCount: number;
  techStackSummary: string;
  hasAvatar: boolean;
  toolsEnabled: string[];
  toolsAvailableCount: number;
}

async function gatherStatus(cwd: string): Promise<StatusSnapshot> {
  const projectName = cwd.split("/").filter(Boolean).pop() ?? "unknown";
  const claudeRoot = join(cwd, ".claude");
  const hasAvatar = await pathExists(claudeRoot);
  if (!hasAvatar) {
    return {
      projectName,
      cliVersion: readCliVersion(),
      packVersion: null,
      pendingCount: 0,
      backupCount: 0,
      techStackSummary: "(Avatar chưa init)",
      hasAvatar: false,
      toolsEnabled: [],
      toolsAvailableCount: 0,
    };
  }

  // v1.13.0 perf: 4 probes độc lập (pack, pending, backup, tech-stack) chạy
  // parallel qua Promise.all thay vì sequential. Save ~100-200ms trên repo
  // có nhiều file/backup. Mỗi probe self-catch (.catch → safe fallback) để
  // 1 probe fail không kill cả status command.
  const pendingDir = join(claudeRoot, "_pending");
  const [packVersion, pendingCount, backupCount, techStackSummary, tools] = await Promise.all([
    (async (): Promise<string | null> => {
      // v3: pack là tarball (không git) → đọc version từ manifest. null nếu chưa cài.
      if (!(await pathExists(join(claudeRoot, "pack")))) return null;
      return readPinnedPackVersion(cwd).catch(() => null);
    })(),
    (async (): Promise<number> => {
      if (!(await pathExists(pendingDir))) return 0;
      const entries = await fs.readdir(pendingDir);
      return entries.filter((n) => n.endsWith(".diff.md")).length;
    })(),
    listBackups(cwd)
      .then((b) => b.length)
      .catch(() => 0),
    readTechStackFirstLine(claudeRoot).catch(() => "(read error)"),
    discoverEnabledAndAvailableTools(cwd).catch(() => ({ available: [], enabled: [] })),
  ]);

  return {
    projectName,
    cliVersion: readCliVersion(),
    packVersion,
    pendingCount,
    backupCount,
    techStackSummary,
    hasAvatar: true,
    toolsEnabled: tools.enabled,
    toolsAvailableCount: tools.available.length,
  };
}

/** Trạng thái login cho panel của extension — KHÔNG BAO GIỜ chứa chính token. */
export interface AuthStatus {
  has_token: boolean;
  email: string | null;
  expires_in_hours: number | null;
  error: string | null;
}

/**
 * Dựng `auth` từ id_token đã lưu — hàm THUẦN để unit-test được cả nhánh hỏng.
 *
 * `expiresAtSec` = claim `exp` của id_token (giây epoch). Trả giờ còn lại làm tròn
 * 1 chữ số; đã hết hạn ⇒ 0, không trả số âm (panel chỉ cần biết "phải login lại").
 */
export function buildAuthStatus(
  email: string | null,
  expiresAtSec: number | null,
  nowMs: number = Date.now(),
): AuthStatus {
  if (email === null) return { has_token: false, email: null, expires_in_hours: null, error: null };
  if (expiresAtSec === null) {
    return { has_token: true, email, expires_in_hours: null, error: "id_token không đọc được exp" };
  }
  const remainingHours = (expiresAtSec * 1000 - nowMs) / 3_600_000;
  return {
    has_token: true,
    email,
    expires_in_hours: Math.max(0, Math.round(remainingHours * 10) / 10),
    error: null,
  };
}

/**
 * Đọc `~/.avatar/config.json` để biết đang login bằng email nào, token còn bao lâu.
 *
 * ⚠️ CỐ Ý KHÔNG gọi `getValidIdToken()`: hàm đó tự refresh qua mạng và có thể ném
 * khi hết hạn. Panel chỉ cần TRẠNG THÁI, nên đường này thuần đọc đĩa ⇒ fail-soft
 * thật sự (mất mạng vẫn exit 0), và không bao giờ tự ý mở luồng login.
 */
async function gatherAuthStatus(): Promise<AuthStatus> {
  try {
    const config = await readUserConfig();
    if (!config) return buildAuthStatus(null, null);
    const { decodeIdToken } = await import("../lib/google-oauth-device-flow.js");
    let exp: number | null = null;
    try {
      exp = decodeIdToken(config.id_token).exp;
    } catch {
      exp = null; // token hỏng/không parse được → báo qua `error`, không ném
    }
    return buildAuthStatus(config.email, exp);
  } catch (err) {
    return {
      has_token: false,
      email: null,
      expires_in_hours: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

async function readTechStackFirstLine(claudeRoot: string): Promise<string> {
  const techStackPath = join(claudeRoot, "project", "tech-stack.md");
  if (!(await pathExists(techStackPath))) return "(no tech-stack.md)";
  const content = await readText(techStackPath);
  const firstNonHeaderLine = content
    .split("\n")
    .find((l) => l.trim() && !l.startsWith("#") && !l.startsWith(">"));
  return firstNonHeaderLine?.trim() ?? "(empty)";
}

// "prompt-scoring (1/1 available)" hoặc "none (1 available)" / "none".
function formatTools(s: StatusSnapshot): string {
  if (s.toolsEnabled.length > 0) {
    return `${s.toolsEnabled.join(", ")} (${s.toolsEnabled.length}/${s.toolsAvailableCount} available)`;
  }
  if (s.toolsAvailableCount > 0) {
    return `none enabled (${s.toolsAvailableCount} available — avatar tool list)`;
  }
  return "none";
}

function renderStatusBox(s: StatusSnapshot): void {
  const lines = [
    `${chalk.bold("Avatar Status")} · ${chalk.cyan(s.projectName)}`,
    "─".repeat(48),
    `${chalk.dim("CLI version:")}        ${s.cliVersion}`,
    `${chalk.dim("Pack version:")}       ${s.packVersion ?? chalk.yellow("not installed")}`,
    `${chalk.dim("Pending changes:")}    ${s.pendingCount}${s.pendingCount > 0 ? chalk.dim(" (avatar review)") : ""}`,
    `${chalk.dim("Backups:")}            ${s.backupCount}`,
    `${chalk.dim("Tech stack:")}         ${s.techStackSummary}`,
    `${chalk.dim("Tools:")}           ${formatTools(s)}`,
  ];
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}
